import React, { useState, useEffect, Fragment } from 'react';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import API from '../../services/API';
import { Tag } from 'antd';
import { Spinner, Row, Col, Input, Button, Card, CardBody, CardHeader } from 'reactstrap';
import dayjs from 'dayjs';

const AuditTrailTable = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [userId, setUserId] = useState('');
    const [action, setAction] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [limit, setLimit] = useState(20);
    const [offset, setOffset] = useState(0);
    const [totalRows, setTotalRows] = useState(0);

    // Fetch logs from API
    const fetchLogs = async () => {
        setLoading(true);
        setError('');
        try {
            let params = { limit, offset };
            if (userId) params.user_id = userId;
            if (action) params.action = action;
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;
    
            const token = localStorage.getItem('token');
            const resp = await API.get('/portal/audit/log', { 
                params, 
                headers: { 'token': token }
            });
            if (resp.data && resp.data.code === 1000) {
                setLogs(resp.data.data || []);
                setTotalRows((resp.data.data && resp.data.data.length) || 0);
            } else {
                setError(resp.data.message || 'Failed to fetch audit logs');
            }
        } catch (e) {
            console.log(e)
            setError('Error fetching audit logs');
        } finally {
            setLoading(false);
        }
    };
    
    useEffect(() => {
        fetchLogs();
        // eslint-disable-next-line
    }, [userId, action, startDate, endDate, limit, offset]);

    // Pagination handler for DataTable
    const handlePageChange = (page) => {
        setOffset((page - 1) * limit);
    };
    const handlePerRowsChange = (newLimit, page) => {
        setLimit(newLimit);
        setOffset((page - 1) * newLimit);
    };

    // Table columns definition
    const columns = [
        {
            name: "#",
            cell: (row, idx) => offset + idx + 1,
            width: "60px",
            center: true,
        },
        {
            name: "Timestamp",
            selector: row => dayjs(row.timestamp).format('YYYY-MM-DD HH:mm:ss'),
            sortable: true,
            minWidth: "180px",
        },
        {
            name: "Role",
            selector: row => row.user_role,
            minWidth: "110px",
            cell: row => <Tag color="geekblue">{row.user_role || '-'}</Tag>,
        },
        {
            name: "Action",
            selector: row => row.action,
            minWidth: "120px",
            cell: row => <Tag color="blue">{row.action}</Tag>,
        },
        {
            name: "Details",
            selector: row => row.details,
            wrap: true,
            grow: 2,
        },
    ];

    return (
        <Fragment>
            <div className="container-fluid">
                <Row className="mb-3 g-2">
                    <Col xs={12} sm={6} md={2}>
                        <Input
                            type="text"
                            placeholder="User ID"
                            value={userId}
                            onChange={e => setUserId(e.target.value)}
                        />
                    </Col>
                    <Col xs={12} sm={6} md={2}>
                        <Input
                            type="text"
                            placeholder="Action"
                            value={action}
                            onChange={e => setAction(e.target.value)}
                        />
                    </Col>
                    <Col xs={12} sm={6} md={2}>
                        <Input
                            type="date"
                            value={startDate}
                            onChange={e => setStartDate(e.target.value)}
                        />
                    </Col>
                    <Col xs={12} sm={6} md={2}>
                        <Input
                            type="date"
                            value={endDate}
                            onChange={e => setEndDate(e.target.value)}
                        />
                    </Col>
                    <Col xs={12} sm={6} md={2}>
                        <Button color="primary" block onClick={() => { setOffset(0); fetchLogs(); }}>
                            Search
                        </Button>
                    </Col>
                </Row>
                <Card className="rounded-25">
                    <CardHeader className="rounded-25 text-red">
                        <h5>Audit Trail Logs</h5>
                    </CardHeader>
                    <CardBody>
                        {loading && <Spinner color="primary" />}
                        {error && <div className="alert alert-danger">{error}</div>}
                        <div className="table-responsive">
                            <DataTable
                                columns={columns}
                                data={logs}
                                progressPending={loading}
                                striped
                                persistTableHead
                                noHeader
                                pagination
                                paginationServer
                                paginationPerPage={limit}
                                paginationTotalRows={totalRows}
                                paginationDefaultPage={offset / limit + 1}
                                onChangeRowsPerPage={handlePerRowsChange}
                                onChangePage={handlePageChange}
                                highlightOnHover
                                pointerOnHover
                                responsive
                            />
                        </div>
                    </CardBody>
                </Card>
            </div>
        </Fragment>
    );
};

export default AuditTrailTable;
